import { Plus, Trash2, FileSpreadsheet, Database, ArrowRight } from 'lucide-react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { ConnectorStatusBadge } from '@/features/connectors/components/ConnectorStatusBadge';
import { useConnectors, useDeleteConnector } from '@/features/connectors/hooks';
import type { Connector } from '@/features/connectors/types';
import type { ApiError } from '@/lib/errors';

export function ConnectorsListPage(): JSX.Element {
  const connectors = useConnectors();
  const remove = useDeleteConnector();

  function handleDelete(event: MouseEvent, connector: Connector): void {
    event.preventDefault();
    event.stopPropagation();
    const count = connector.datasets_count ?? 0;
    const detail = count > 0 ? ` and its ${count} dataset(s)` : '';
    if (
      window.confirm(
        `Delete “${connector.name}”${detail}? This permanently removes the source and all related ` +
          `data — uploaded tables, quality metrics, and duplicate-match results. This cannot be undone.`,
      )
    ) {
      remove.mutate(connector.id);
    }
  }

  const total = connectors.data?.length ?? 0;
  const ready = connectors.data?.filter((c) => c.status === 'ready').length ?? 0;
  const errored = connectors.data?.filter((c) => c.status === 'error').length ?? 0;

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow eyebrow-dot mb-2.5">
            <span>ADMIN</span>
          </div>
          <h1 className="h-display">Data Sources</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
            Connect a database or upload a file, then manage the resulting datasets.
          </p>
        </div>
        <Link to="/connectors/new" className="btn-primary group h-10 px-4 text-sm">
          <Plus className="h-4 w-4" />
          Add connector
        </Link>
      </div>

      {connectors.data && total > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatTile label="TOTAL SOURCES" value={total} caption={`${ready} ready`} tone="brand" />
          <StatTile
            label="ERRORED"
            value={errored}
            caption={errored > 0 ? 'needs attention' : 'all clear'}
            tone={errored > 0 ? 'crimson' : 'neutral'}
          />
          <StatTile
            label="DATASETS"
            value={connectors.data.reduce((acc, c) => acc + (c.datasets_count ?? 0), 0)}
            caption="across all sources"
            tone="violet"
          />
        </div>
      )}

      {connectors.isPending ? (
        <Spinner label="Loading connectors…" />
      ) : connectors.isError ? (
        <ErrorState error={connectors.error as ApiError} />
      ) : connectors.data.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {connectors.data.map((connector) => (
            <ConnectorCard
              key={connector.id}
              connector={connector}
              onDelete={(e) => handleDelete(e, connector)}
              deleting={remove.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  caption,
  tone,
}: {
  label: string;
  value: number;
  caption: string;
  tone: 'brand' | 'violet' | 'crimson' | 'neutral';
}): JSX.Element {
  const accent =
    tone === 'crimson'
      ? 'text-accent-crimson'
      : tone === 'violet'
        ? 'text-accent-violet'
        : tone === 'brand'
          ? 'text-brand-600'
          : 'text-ink-700 dark:text-ink-200';
  return (
    <div className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 p-4 shadow-soft">
      <div className="font-mono text-[10px] font-semibold tracking-[0.18em] text-ink-500 dark:text-ink-400">
        {label}
      </div>
      <div className="mt-1.5 flex items-end gap-2">
        <div className={`font-display text-3xl font-semibold tabular-nums ${accent}`}>{value}</div>
        <div className="mb-1 text-xs text-ink-500 dark:text-ink-400">{caption}</div>
      </div>
    </div>
  );
}

function ConnectorCard({
  connector,
  onDelete,
  deleting,
}: {
  connector: Connector;
  onDelete: (e: MouseEvent) => void;
  deleting: boolean;
}): JSX.Element {
  const Icon = connector.type === 'file' ? FileSpreadsheet : Database;
  const accent =
    connector.type === 'file'
      ? { bg: 'bg-accent-amber/10', fg: 'text-accent-amber' }
      : { bg: 'bg-accent-violet/10', fg: 'text-accent-violet' };

  return (
    <Link
      to={`/connectors/${connector.id}`}
      className="group relative overflow-hidden rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 p-5 shadow-soft transition hover:-translate-y-0.5 hover:border-ink-300 hover:shadow-elevated"
    >
      <div className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-brand-100/40 opacity-0 blur-2xl transition group-hover:opacity-100" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent.bg} ${accent.fg}`}
          >
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] tracking-[0.18em] text-ink-500 dark:text-ink-400">
                {connector.type.toUpperCase()}
              </span>
              <ConnectorStatusBadge status={connector.status} />
            </div>
            <p className="mt-1 truncate font-display text-sm font-semibold text-ink-900 dark:text-ink-50">
              {connector.name}
            </p>
            <p className="mt-0.5 truncate text-xs text-ink-500 dark:text-ink-400">
              {connector.datasets_count !== undefined && (
                <>
                  <span className="tabular-nums font-medium text-ink-700 dark:text-ink-200">
                    {connector.datasets_count}
                  </span>{' '}
                  dataset(s)
                </>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          title="Delete data source and all related data"
          disabled={deleting}
          onClick={onDelete}
          className="rounded-md p-1.5 text-ink-400 dark:text-ink-500 transition hover:bg-accent-crimson/10 hover:text-accent-crimson disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {connector.last_error && (
        <p className="relative mt-3 truncate rounded-md border border-accent-crimson/20 bg-accent-crimson/[0.05] px-2.5 py-1.5 font-mono text-[11px] text-accent-crimson">
          {connector.last_error}
        </p>
      )}
      <div className="relative mt-4 flex items-center justify-between border-t border-ink-100 dark:border-white/5 pt-3">
        <span className="font-mono text-[10px] tracking-[0.18em] text-ink-500 dark:text-ink-400">
          ID · {connector.id.slice(0, 8)}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700">
          Open
          <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-dashed border-ink-300 dark:border-white/15 bg-white dark:bg-ink-900 p-12 text-center shadow-soft">
      <div className="pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-brand-200/40 blur-3xl" />
      <div className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-400 to-brand-700 text-white shadow-glow">
        <Plus className="h-5 w-5" />
      </div>
      <div className="relative mt-4 font-display text-base font-semibold text-ink-900 dark:text-ink-50">
        No data sources yet
      </div>
      <p className="relative mx-auto mt-1 max-w-md text-sm text-ink-500 dark:text-ink-400">
        Add a database connection or upload a spreadsheet to get started. nlytics will profile and
        catalogue the data automatically.
      </p>
      <Link
        to="/connectors/new"
        className="btn-primary group relative mt-5 inline-flex h-10 px-4 text-sm"
      >
        <Plus className="h-4 w-4" />
        Add your first source
      </Link>
    </div>
  );
}
